import { DateRange, DeviceCategory, DatePickerPreset } from "@bebit/types";
import { getDefaultPeriod, isHighlightablePreset } from "@bebit/utils";
import { ref, Ref, watch, onMounted, computed } from "vue";
import { useDeviceCategoryFromStorages } from "@host/composables/storages/useDeviceCategoryFromStorages";
import { usePeriodFromStorages } from "@host/composables/storages/usePeriodFromStorages";
import { usePresetFromStorages } from "@host/composables/storages/usePresetFromStorages";
import { useUrlParams } from "@host/composables/url-params/useUrlParams";

export function useReport() {
  const { deviceCategory, isDeviceCategoryInitialized } = useDeviceCategory();
  const { selectedPeriod, selectedPreset, isPeriodInitialized } = usePeriod();
  const isReportInitialized = computed(() => isDeviceCategoryInitialized.value && isPeriodInitialized.value);

  return { deviceCategory, selectedPeriod, selectedPreset, isReportInitialized };
}

function useDeviceCategory() {
  const { deviceCategoryFromUrl, setDeviceCategoryToUrl } = useUrlParams();
  const { deviceCategoryFromStorages, saveDeviceCategoryToStorages } = useDeviceCategoryFromStorages();
  const deviceCategory = ref(DeviceCategory.PC);
  const isDeviceCategoryInitialized = ref(false);

  onMounted(async () => {
    deviceCategory.value = deviceCategoryFromUrl.value || deviceCategoryFromStorages.value || DeviceCategory.PC;

    saveDeviceCategoryToUrlAndStorages();
    isDeviceCategoryInitialized.value = true;
  });

  function saveDeviceCategoryToUrlAndStorages() {
    setDeviceCategoryToUrl(deviceCategory.value);
    saveDeviceCategoryToStorages(deviceCategory.value);
  }

  watch(deviceCategory, () => {
    saveDeviceCategoryToUrlAndStorages();
  });

  return { deviceCategory, isDeviceCategoryInitialized };
}

// eslint-disable-next-line max-lines-per-function
function usePeriod() {
  const selectedPeriod: Ref<DateRange> = ref(getDefaultPeriod());
  const selectedPreset: Ref<DatePickerPreset | undefined> = ref(undefined);
  const isPeriodInitialized = ref(false);

  const { periodFromUrl, setPeriodToUrl, isValidPeriodFromUrl, presetFromUrl, setPresetToUrl } = useUrlParams();
  const { periodFromStorages, savePeriodToStorages, isUsingDefaultPeriod } = usePeriodFromStorages();
  const { presetFromStorages, savePresetToStorages } = usePresetFromStorages();

  const validPeriodFromUrl = computed(() => {
    return isValidPeriodFromUrl.value ? periodFromUrl.value : undefined;
  });

  onMounted(async () => {
    initPresetAndPeriod();
    selectedPreset.value = presetFromUrl.value || presetFromStorages.value || undefined;
    selectedPeriod.value = validPeriodFromUrl.value || periodFromStorages.value || getDefaultPeriod();
    savePeriodToUrlAndStorages();
    isPeriodInitialized.value = true;
  });

  function savePeriodToUrlAndStorages() {
    setPresetOrPeriodToUrl();
    savePresetAndPeriodToStorages();
  }

  function savePresetAndPeriodToStorages() {
    savePresetToStorages(selectedPreset.value);
    savePeriodToStorages(selectedPeriod.value);
  }

  function setPresetOrPeriodToUrl() {
    if (selectedPreset.value && isHighlightablePreset(selectedPreset.value)) setPresetToUrl(selectedPreset.value);
    else setPeriodToUrl(selectedPeriod.value);
  }

  function initPresetAndPeriod() {
    if (isUsingDefaultPeriod.value) {
      savePresetToStorages(DatePickerPreset.Last28Days);
    } else {
      if (periodFromUrl.value) savePresetToStorages(undefined);
    }
    savePeriodToStorages(periodFromStorages.value || selectedPeriod.value);
  }

  watch([selectedPeriod, selectedPreset], () => {
    savePeriodToUrlAndStorages();
  });

  return { selectedPeriod, selectedPreset, isPeriodInitialized };
}
